import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import InputField from 'dashboard/components/FormControls/InputField';
import PhoneInputField from 'dashboard/components/FormControls/PhoneInputField';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isMaxLength, withMessage } from 'lib/utils/formValidation';

const validateReferenceID = {
  'reference-id': [
    withMessage('Please limit your Reference ID to 255 characters', isMaxLength(255)),
  ],
};

const validate = {
  [PiiAttributes.EmailAddress]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.PhoneNumber] &&
        !(
          allValues[PiiAttributes.NameFirst] &&
          allValues[PiiAttributes.NameLast] &&
          allValues[PiiAttributes.AddressCity] &&
          allValues[PiiAttributes.AddressSubdivision]
        )
      ) {
        return 'Please enter an email address, phone number, or complete name and address details';
      }
    },
  ],
  [PiiAttributes.PhoneNumber]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.EmailAddress] &&
        !(
          allValues[PiiAttributes.NameFirst] &&
          allValues[PiiAttributes.NameLast] &&
          allValues[PiiAttributes.AddressCity] &&
          allValues[PiiAttributes.AddressSubdivision]
        )
      ) {
        return 'Please enter a phone number, email address, or complete name and address details';
      }
    },
  ],
  [PiiAttributes.NameFirst]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.EmailAddress] &&
        !allValues[PiiAttributes.PhoneNumber] &&
        (allValues[PiiAttributes.NameLast] ||
          allValues[PiiAttributes.AddressCity] ||
          allValues[PiiAttributes.AddressSubdivision])
      ) {
        return 'Please enter a first name to complete the name and address details';
      }
    },
  ],
  [PiiAttributes.NameLast]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.EmailAddress] &&
        !allValues[PiiAttributes.PhoneNumber] &&
        (allValues[PiiAttributes.NameFirst] ||
          allValues[PiiAttributes.AddressCity] ||
          allValues[PiiAttributes.AddressSubdivision])
      ) {
        return 'Please enter a last name to complete the name and address details';
      }
    },
  ],
  [PiiAttributes.AddressCity]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.EmailAddress] &&
        !allValues[PiiAttributes.PhoneNumber] &&
        (allValues[PiiAttributes.NameFirst] ||
          allValues[PiiAttributes.NameLast] ||
          allValues[PiiAttributes.AddressSubdivision])
      ) {
        return 'Please enter a locality to complete the name and address details';
      }
    },
  ],
  [PiiAttributes.AddressSubdivision]: [
    (value: any, allValues: { [x: string]: any }) => {
      if (
        !value &&
        !allValues[PiiAttributes.EmailAddress] &&
        !allValues[PiiAttributes.PhoneNumber] &&
        (allValues[PiiAttributes.NameFirst] ||
          allValues[PiiAttributes.NameLast] ||
          allValues[PiiAttributes.AddressCity])
      ) {
        return 'Please enter a state, region or subdivision to complete the name and address details';
      }
    },
  ],
};

interface FormCreateReportProfileNonAuthoritativeProps {
  hideReferenceId: boolean;
  handleCancel: () => void;
  readOnly?: boolean;
}

const FormCreateReportProfileNonAuthoritative = ({
  submitting,
  hideReferenceId,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportProfileNonAuthoritativeProps &
  InjectedFormProps<
    FormCreateReportProfileNonAuthoritativeData,
    FormCreateReportProfileNonAuthoritativeProps
  >) => {
  return (
    <>
      <Form onSubmit={handleSubmit}>
        <FormSection cols={1}>
          {!hideReferenceId && (
            <Field
              id='create-report-profile-non-authoritative__reference-id'
              label='Reference ID (Optional)'
              name='reference-id'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validateReferenceID['reference-id']}
            />
          )}
          <Field
            id='create-report-profile-non-authoritative__email_address'
            label='Email Address'
            name={PiiAttributes.EmailAddress}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            validate={validate[PiiAttributes.EmailAddress]}
            placeholder='Email Address'
          />
        </FormSection>
      </Form>

      <Form onSubmit={handleSubmit}>
        <FormSection cols={1}>
          <Field
            id='create-report-profile-non-authoritative__address'
            label='Phone Number'
            name={PiiAttributes.PhoneNumber}
            type='text'
            disabled={submitting || readOnly}
            component={PhoneInputField}
            validate={validate[PiiAttributes.PhoneNumber]}
            placeholder='Phone Number'
          />
        </FormSection>
      </Form>

      <Form onSubmit={handleSubmit}>
        <FormSection cols={2}>
          <Field
            id='create-report-profile-non-authoritative__name-first'
            label='First name'
            name={PiiAttributes.NameFirst}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            placeholder='First name'
            validate={validate[PiiAttributes.NameFirst]}
          />
          <Field
            id='create-report-profile-non-authoritative__name-middle'
            label='Middle name (Optional)'
            name={PiiAttributes.NameMiddle}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            placeholder='Middle name'
          />
          <Field
            id='create-report-profile-non-authoritative__name-last'
            label='Last name'
            name={PiiAttributes.NameLast}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            placeholder='Last name'
            validate={validate[PiiAttributes.NameLast]}
          />
        </FormSection>
        <FormSection cols={2}>
          <Field
            id='create-report-profile-non-authoritative__address-city'
            label='Locality'
            name={PiiAttributes.AddressCity}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            placeholder='City or Locality'
            validate={validate[PiiAttributes.AddressCity]}
          />
          <Field
            id='create-report-profile-non-authoritative__address-subdivision'
            label='Subdivision'
            name={PiiAttributes.AddressSubdivision}
            type='text'
            disabled={submitting || readOnly}
            component={InputField}
            placeholder='State, Region, or Subdivision'
            validate={validate[PiiAttributes.AddressSubdivision]}
          />
        </FormSection>
        <HStack equal={true}>
          <Button
            variant='basic'
            id='create-report-profile-non-authoritative__cancel'
            disabled={submitting}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            id='create-report-profile-non-authoritative__submit'
            disabled={submitting}
            isPending={submitting}
          >
            Create Report
          </Button>
        </HStack>
      </Form>
    </>
  );
};

export interface FormCreateReportProfileNonAuthoritativeData {
  'reference-id'?: string;
  [PiiAttributes.NameFirst]?: string;
  [PiiAttributes.NameMiddle]?: string;
  [PiiAttributes.NameLast]?: string;
  [PiiAttributes.AddressSubdivision]?: string;
  [PiiAttributes.AddressPostalCode]?: string;
  [PiiAttributes.AddressCity]?: string;
  [PiiAttributes.EmailAddress]?: string;
  [PiiAttributes.PhoneNumber]?: string;
}

export const FORM_CREATE_REPORT_PROFILE_NON_AUTHORITATIVE_NAME =
  'formCreateReportProfileNonAuthoritativeData';

export default reduxForm<
  FormCreateReportProfileNonAuthoritativeData,
  FormCreateReportProfileNonAuthoritativeProps
>({
  form: FORM_CREATE_REPORT_PROFILE_NON_AUTHORITATIVE_NAME,
})(FormCreateReportProfileNonAuthoritative);
